import React from "react";
import { Card, CardContent } from "../../ui/card";
import { Headline, Kpi, PainBanner, Pill, SectionTitle } from "./PersonaUi";
import { OBS, REAL, SHORT, TILTS, effectiveManagers } from "./allocatorData";

const heat = (v) => (v >= 50 ? "bg-red-500/60" : v >= 35 ? "bg-amber-500/50" : v >= 20 ? "bg-amber-500/20" : "bg-zinc-800");
const REPL = ["PRBLX", "JENSX", "FMAGX"];

export default function AllocLookthrough() {
  const L = REAL.lineup;
  const { rho, eff } = effectiveManagers();
  const pairs = [];
  L.forEach((a, i) => L.slice(i + 1).forEach((b) => pairs.push([a, b, REAL.overlap[a][b]])));
  const worst = pairs.sort((x, y) => y[2] - x[2]).slice(0, 3);
  const lo = Math.floor(Math.min(...worst.map((w) => w[2])));
  const hi = Math.floor(Math.max(...worst.map((w) => w[2])));
  const swapOut = worst[0].map((f) => REAL.grades[f]).filter((g) => g && g.fund).sort((a, b) => (a.ungraded_reason === "index_like" ? -1 : 0) - (b.ungraded_reason === "index_like" ? -1 : 0))[0].fund;
  const avgOv = (f) => REAL.candidate_overlap[f] ? Object.values(REAL.candidate_overlap[f]).reduce((s, x) => s + x, 0) / L.length : 0;
  return (
    <div className="space-y-4" data-testid="persona-alloc-lookthrough">
      <PainBanner testid="persona-alloc-lookthrough-pain" persona="LONG-ONLY ALLOCATOR · LINEUP LOOK-THROUGH"
        pain="I picked 8 'different' managers — am I diversified, or paying 8 fees for the same stocks?"
        answer="holdings overlap matrix + what the lineup really owns" />
      <div>
        <Headline testid="alloc-look-headline">Your 8 managers behave like ~{eff.toFixed(1)} independent ones — 3 growth funds share {lo}–{hi}% of their holdings</Headline>
        <div className="text-sm text-muted-foreground mt-1">Sample "Core US Equity" lineup · equal-weighted · real N-PORT holdings (latest filing per fund, Mar–May 2026)</div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testid="alloc-look-kpi-eff" label="Effective managers" value={eff.toFixed(1)} tone="text-amber-400" sub={`of ${L.length} · avg pairwise overlap ${(rho * 100).toFixed(0)}%`} />
        <Kpi testid="alloc-look-kpi-top10" label="Top-10 stocks = lineup" value={`${REAL.top10_combined_pct}%`} sub="share of combined assets" />
        <Kpi testid="alloc-look-kpi-nvda" label="Most shared name" value={REAL.shared[0].symbol} sub={`${REAL.shared[0].lineup_w}% of lineup · in ${REAL.shared[0].n_funds} of ${L.length} funds`} />
        <Kpi testid="alloc-look-kpi-pair" label="Most redundant pair" value={`${worst[0][2]}%`} tone="text-red-400" sub={`${worst[0][0]} × ${worst[0][1]}`} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <Card className="xl:col-span-3"><CardContent className="pt-4 overflow-x-auto">
          <SectionTitle>Holdings overlap (% of portfolio held in common)</SectionTitle>
          <table className="text-[11px] font-mono" data-testid="alloc-look-matrix">
            <thead><tr><th />{L.map((f) => <th key={f} className="px-1 py-1 text-muted-foreground font-normal">{f}</th>)}</tr></thead>
            <tbody>
              {L.map((a) => (
                <tr key={a}>
                  <td className="pr-2 py-0.5 text-muted-foreground whitespace-nowrap">{a} <span className="text-[9px]">{SHORT[a]}</span></td>
                  {L.map((b) => (
                    <td key={b} className="p-0.5">
                      <div className={`w-14 h-9 rounded flex items-center justify-center ${a === b ? "bg-zinc-900 text-zinc-600" : heat(REAL.overlap[a][b])}`} data-testid={`alloc-look-cell-${a}-${b}`}>
                        {a === b ? "—" : REAL.overlap[a][b].toFixed(0)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[10px] text-muted-foreground mt-2">Overlap = Σ min(weight in fund A, weight in fund B). Above ~50% two funds are largely the same portfolio.</div>
        </CardContent></Card>
        <div className="xl:col-span-2 space-y-4">
          <Card><CardContent className="pt-4">
            <SectionTitle>What the lineup really owns</SectionTitle>
            <div className="space-y-1.5" data-testid="alloc-look-shared">
              {REAL.shared.slice(0, 8).map((s) => (
                <div key={s.symbol} className="flex items-center gap-2 text-xs">
                  <span className="font-mono font-bold w-14">{s.symbol}</span>
                  <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full bg-sky-500/70" style={{ width: `${(s.lineup_w / 8) * 100}%` }} /></div>
                  <span className="font-mono w-12 text-right">{s.lineup_w}%</span>
                  <span className="text-[10px] text-muted-foreground w-16 text-right">{s.n_funds}/{L.length} funds</span>
                </div>
              ))}
            </div>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <SectionTitle right={<Pill tone="muted">SAMPLE</Pill>}>Combined factor tilt vs S&P 500</SectionTitle>
            <div className="space-y-1.5" data-testid="alloc-look-tilts">
              {TILTS.map((t) => (
                <div key={t.f} className="flex items-center gap-2 text-xs">
                  <span className="w-28 text-muted-foreground">{t.f}</span>
                  <div className="flex-1 h-2 relative bg-zinc-800 rounded-full">
                    <div className="absolute top-0 bottom-0 left-1/2 w-px bg-zinc-500" />
                    <div className={`absolute top-0 bottom-0 rounded-full ${t.v >= 0 ? "bg-violet-400/70" : "bg-amber-400/70"}`}
                      style={t.v >= 0 ? { left: "50%", width: `${t.v * 50}%` } : { right: "50%", width: `${-t.v * 50}%` }} />
                  </div>
                  <span className="font-mono w-12 text-right">{t.v > 0 ? "+" : ""}{t.v.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </div>
      </div>
      <Card data-testid="alloc-look-replace"><CardContent className="pt-4">
        <SectionTitle right={<Pill tone="good">REPLACEMENT IDEAS</Pill>}>Swap {swapOut} (index-like, {REAL.overlap[worst[0][0]][worst[0][1]].toFixed(0)}% overlap with {worst[0].find((f) => f !== swapOut)}) for a fund that adds something different</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {REPL.map((f) => {
            const g = REAL.grades[f];
            return (
              <div key={f} className="rounded border border-border/70 p-3" data-testid={`alloc-look-repl-${f}`}>
                <div className="flex items-center justify-between"><span className="font-mono font-bold">{f}</span><Pill tone="good">GRADE {g.letter}</Pill></div>
                <div className="text-[11px] text-muted-foreground">{SHORT[f]}</div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
                  <div><div className="text-muted-foreground">avg overlap w/ lineup</div><div className="font-mono font-bold">{avgOv(f).toFixed(0)}%</div></div>
                  <div><div className="text-muted-foreground">active share</div><div className="font-mono font-bold">{(g.active_share * 100).toFixed(0)}%</div></div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-[10px] text-muted-foreground mt-2">Real overlap & grades ({OBS}). Effective managers = n ÷ (1 + (n−1)·avg overlap), a holdings-based diversification proxy.</div>
      </CardContent></Card>
    </div>
  );
}
